import { useState, useEffect } from 'react';
import { therapyService, WeeklyTrend, Streak } from '../services/therapyService';
import DailyCheckInModal from '../components/therapy/DailyCheckInModal';
import PageHeader from '../components/PageHeader';
import { Flame, TrendingUp, MessageCircle, Calendar, Smile, Meh, Frown } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function EmotionalWellbeingPage() {
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [todayCheckIn, setTodayCheckIn] = useState<any>(null);
  const [weeklyTrend, setWeeklyTrend] = useState<WeeklyTrend | null>(null);
  const [streak, setStreak] = useState<Streak | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [checkIn, trend, streakData] = await Promise.all([
        therapyService.getTodayCheckIn(),
        therapyService.getWeeklyTrend(),
        therapyService.getCheckInStreak()
      ]);
      
      setTodayCheckIn(checkIn);
      setWeeklyTrend(trend);
      setStreak(streakData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMoodLabel = (mood: string) => {
    const labelMap: Record<string, string> = {
      'GREAT': 'Excelent',
      'GOOD': 'Bine',
      'OKAY': 'OK',
      'BAD': 'Rău',
      'TERRIBLE': 'Foarte Rău'
    };
    return labelMap[mood] || mood;
  };

  const getMoodIcon = (mood: string) => {
    const moodMap: Record<string, any> = {
      'GREAT': Smile,
      'GOOD': Smile,
      'OKAY': Meh,
      'BAD': Frown,
      'TERRIBLE': Frown
    };
    return moodMap[mood] || Meh;
  };

  const getMoodColor = (mood: string) => {
    const colorMap: Record<string, string> = {
      'GREAT': 'bg-green-100 text-green-700 border-green-300',
      'GOOD': 'bg-blue-100 text-blue-700 border-blue-300',
      'OKAY': 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'BAD': 'bg-orange-100 text-orange-700 border-orange-300',
      'TERRIBLE': 'bg-red-100 text-red-700 border-red-300'
    };
    return colorMap[mood] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <PageHeader title="Jurnal Emoțional" />
        <div className="p-4">
          <div className="text-center py-8">Se încarcă...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader title="Jurnal Emoțional" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Check-in Today Card */}
        {!todayCheckIn ? (
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-3xl shadow-2xl p-8 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold mb-2">Nu ai făcut check-in astăzi!</h2>
                <p className="text-purple-100 text-lg">Cum te simți? Durează doar 2 minute.</p>
              </div>
              <div className="w-32 h-32 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Smile className="w-16 h-16 text-white/80" />
              </div>
            </div>
            <button
              onClick={() => setShowCheckInModal(true)}
              className="mt-6 px-8 py-4 bg-white text-purple-600 rounded-xl font-semibold hover:shadow-lg transition-all inline-flex items-center gap-2"
            >
              <Calendar className="w-5 h-5" />
              Fă Check-in Acum
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-3xl shadow-xl p-6 border-2 border-purple-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Check-in Astăzi</h3>
              <span className={`px-4 py-2 rounded-full font-semibold border-2 flex items-center gap-2 ${getMoodColor(todayCheckIn.mood)}`}>
                {(() => {
                  const MoodIcon = getMoodIcon(todayCheckIn.mood);
                  return <MoodIcon className="w-5 h-5" />;
                })()}
                {getMoodLabel(todayCheckIn.mood)}
              </span>
            </div>
            
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600 mb-2">Emoții</p>
                <div className="flex flex-wrap gap-2">
                  {todayCheckIn.emotions.map((emotion: string) => (
                    <span key={emotion} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                      {emotion}
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-600 mb-2">Intensitate</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-400 via-yellow-400 to-red-500"
                      style={{ width: `${(todayCheckIn.intensity / 10) * 100}%` }}
                    />
                  </div>
                  <span className="text-2xl font-bold text-purple-600">{todayCheckIn.intensity}/10</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Streak Card */}
          <div className="bg-gradient-to-br from-orange-400 to-red-500 rounded-2xl shadow-xl p-6 text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Flame className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-lg">Streak Actual</h3>
            </div>
            <div className="text-5xl font-bold mb-2">{streak?.currentStreak || 0}</div>
            <p className="text-orange-100">zile consecutive</p>
            <div className="mt-4 pt-4 border-t border-white/20">
              <p className="text-sm text-orange-100">Record: {streak?.longestStreak || 0} zile</p>
            </div>
          </div>

          {/* Average Intensity Card */}
          <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-purple-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-bold text-lg text-gray-900">Intensitate Medie</h3>
            </div>
            <div className="text-5xl font-bold text-purple-600 mb-2">
              {weeklyTrend?.stats.avgIntensity.toFixed(1) || '0.0'}
            </div>
            <p className="text-gray-600">ultimele 7 zile</p>
          </div>

          {/* Check-ins Count */}
          <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-blue-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-bold text-lg text-gray-900">Check-ins</h3>
            </div>
            <div className="text-5xl font-bold text-blue-600 mb-2">
              {weeklyTrend?.stats.checkInCount || 0}
            </div>
            <p className="text-gray-600">în ultimele 7 zile</p>
          </div>
        </div>

        {/* Weekly Trend Chart */}
        {weeklyTrend && weeklyTrend.trend.length > 0 && (
          <div className="bg-white rounded-3xl shadow-xl p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Trend Săptămânal</h3>
            <div className="space-y-4">
              {weeklyTrend.trend.map((day) => {
                const MoodIcon = getMoodIcon(day.mood);
                // Fix timezone issue: append T12:00:00 to avoid UTC midnight shifting to previous day
                const dateObj = new Date(day.date.includes('T') ? day.date : day.date + 'T12:00:00');
                return (
                  <div key={day.date} className="flex items-center gap-4">
                    <div className="w-24 text-sm text-gray-600 font-medium">
                      {dateObj.toLocaleDateString('ro-RO', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </div>
                    <div className="flex-1 flex items-center gap-3">
                      <div className="flex-1 h-12 bg-gray-100 rounded-xl overflow-hidden relative">
                        <div
                          className={`h-full ${getMoodColor(day.mood).split(' ')[0]} transition-all flex items-center px-3`}
                          style={{ width: `${(day.intensity / 10) * 100}%` }}
                        >
                          <span className="text-sm font-semibold">{day.intensity}/10</span>
                        </div>
                      </div>
                      <MoodIcon className="w-6 h-6" />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Stats Summary */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Dispoziție Dominantă</p>
                  <p className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    {(() => {
                      const DominantIcon = getMoodIcon(weeklyTrend.stats.dominantMood);
                      return <DominantIcon className="w-5 h-5" />;
                    })()}
                    {getMoodLabel(weeklyTrend.stats.dominantMood)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Emoții Comune</p>
                  <div className="flex flex-wrap gap-2">
                    {weeklyTrend.stats.commonEmotions.slice(0, 3).map((emotion) => (
                      <span key={emotion} className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                        {emotion}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <Link
          to="/therapy-chat"
          className="block bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl shadow-xl p-6 text-white hover:shadow-2xl transition-all group"
        >
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 backdrop-blur-sm group-hover:scale-110 transition-transform">
              <MessageCircle className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold mb-2">Chat Terapeutic AI</h3>
              <p className="text-blue-100 text-sm">
                Vorbește cu psihologul virtual 24/7 • Confidențial
              </p>
            </div>
          </div>
        </Link>
      </div>

      {showCheckInModal && (
        <DailyCheckInModal
          onClose={() => setShowCheckInModal(false)}
          onSuccess={() => {
            setShowCheckInModal(false);
            loadData();
          }}
        />
      )}
    </div>
  );
}
